import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { shouldEnableBrowserPwa } from "../services/channelHosts";
import { isStandalonePwa } from "../services/pwaRegister";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "mvk_pwa_install_dismissed";

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const webkit = /WebKit/i.test(ua);
  const notOther = !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Firefox|Edg/i.test(ua);
  return iOS && webkit && notOther;
}

/**
 * Browser-only install banner on PUBLIC_WEB_URL host.
 * Hidden inside MAX, on mvk-max.* host, and when already installed.
 */
export function PwaInstallBanner() {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (!shouldEnableBrowserPwa() || isStandalonePwa()) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch {
      // ignore
    }

    if (isIosSafari()) {
      setIosHint(true);
      setVisible(true);
      return;
    }

    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    return () => window.removeEventListener("beforeinstallprompt", onBip);
  }, []);

  if (!visible || (!deferred && !iosHint)) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
  };

  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice.catch(() => undefined);
    setDeferred(null);
    setVisible(false);
  };

  return (
    <div className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-3 right-3 z-[60] mx-auto max-w-md">
      <div className="flex items-start gap-3 rounded-2xl border border-indigo-100 bg-white/95 p-3 shadow-lg backdrop-blur">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-gray-900">{t("common.pwa_title")}</p>
          <p className="mt-0.5 text-[12px] text-gray-500">
            {iosHint ? t("common.pwa_desc_ios") : t("common.pwa_desc")}
          </p>
          <div className="mt-2 flex gap-2">
            {deferred ? (
              <button
                type="button"
                onClick={() => void install()}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-[13px] text-white"
              >
                {t("common.pwa_install")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={dismiss}
              className="rounded-lg px-3 py-1.5 text-[13px] text-gray-500"
            >
              {t("common.pwa_later")}
            </button>
          </div>
        </div>
        <button type="button" onClick={dismiss} className="p-1 text-gray-400" aria-label={t("common.close")}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
